// API tokens (2026-09-29): a key for an outside app (a bot, a script) to reach
// /api/v1 with exactly what it is given. A role fills the per-kind levels,
// which can then be set one by one; workspaces fence the kind scopes; named
// projects, pages and lists give reach to what sits under them; extras add
// Ask Prime, the library and the schedule. Writes either wait in the Inbox
// (the default) or apply at once. The secret is shown once, as for links.
// A key for Claude Code (2026-10-31) adds `mcp`: the same reach on /api/mcp,
// its changes always cards on a thread of its own, and the sheet shows the
// plugin's .mcp.json, which reads the address and key from the environment.
import { useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { useAllLists, useAllPages, useAllProjects, useWorkspaces } from '@/db/hooks'
import { hashSecret, newSecret, type GrantResource } from '@/kinds/grant'
import { mutate } from '@/runtime'
import { Button, Field, Segmented, Sheet } from './ui'

type Level = 'none' | 'read' | 'write'

/** The kinds a token can reach, as the API names them. */
export const TOKEN_KINDS: { key: string; label: string }[] = [
  { key: 'task', label: 'Tasks' },
  { key: 'project', label: 'Projects' },
  { key: 'page', label: 'Pages' },
  { key: 'list', label: 'Lists' },
  { key: 'date', label: 'Dates' },
  { key: 'person', label: 'People' },
]

const EXTRAS: { scope: string; label: string; hint: string }[] = [
  {
    scope: 'prime',
    label: 'Ask Prime',
    hint: 'Questions answered from what this key can read; changes nothing',
  },
  {
    scope: 'schedule:read',
    label: 'Schedule',
    hint: 'The calendar for the next days, cut to what it can read',
  },
  { scope: 'library:read', label: 'Library', hint: 'Music, films, TV and games, and the listening stats' },
  { scope: 'capture', label: 'Capture', hint: 'Send things to your Inbox, like a Shortcut' },
]

/** The extras a Claude Code key may not hold (grant.Validate): capture files on its own, and Ask Prime keeps the key to what it was made for. */
const NOT_FOR_CLIENTS = new Set(['capture', 'prime'])

/** The .mcp.json a plugin or repo carries: no secret in it, only the names of two variables. */
export const MCP_JSON = JSON.stringify(
  {
    mcpServers: {
      alfieos: {
        type: 'http',
        url: '${ALFIEOS_URL}/api/mcp',
        headers: { Authorization: 'Bearer ${ALFIEOS_TOKEN}' },
      },
    },
  },
  null,
  2,
)

/** The two lines that set those variables, for the shell profile of whoever runs Claude Code. */
export const mcpEnv = (origin: string, secret: string): string =>
  `export ALFIEOS_URL=${origin}\nexport ALFIEOS_TOKEN=${secret}`

const ROLES: { key: string; label: string; levels: Record<string, Level> }[] = [
  {
    key: 'reader',
    label: 'Read everything',
    levels: { task: 'read', project: 'read', page: 'read', list: 'read', date: 'read', person: 'read' },
  },
  {
    key: 'tasks',
    label: 'Tasks and lists',
    levels: { task: 'write', project: 'write', page: 'read', list: 'write', date: 'read', person: 'none' },
  },
  { key: 'none', label: 'Only what I pick', levels: {} },
]

const THINGS = /^(project|page|list|task):([0-9a-f-]{36}):(read|write)$/

/** The role whose levels are the ones set, so the sheet can light it; none when they were set one by one. */
export const roleOf = (levels: Record<string, Level>): string | undefined =>
  ROLES.find((r) => TOKEN_KINDS.every((k) => (levels[k.key] ?? 'none') === (r.levels[k.key] ?? 'none')))?.key

/** One line on what a token reaches, for the list in Settings. Suites by name (`suiteName`), never by slug. */
export const describeToken = (scopes: string[], suiteName: (slug: string) => string = (s) => s): string => {
  if (scopes.some((s) => s.startsWith('files-sync:'))) return 'Two-way sync of one Files folder'
  const kinds = TOKEN_KINDS.flatMap((k) => {
    if (scopes.includes(`${k.key}:write`)) return [`${k.label.toLowerCase()} (change)`]
    if (scopes.includes(`${k.key}:read`)) return [k.label.toLowerCase()]
    return []
  })
  const things = scopes.filter((s) => THINGS.test(s)).length
  const ws = scopes.filter((s) => s.startsWith('ws:')).map((s) => s.slice(3))
  const extras = EXTRAS.filter((e) => scopes.includes(e.scope)).map((e) =>
    e.label.replace(/^[A-Z](?=[a-z])/, (c) => c.toLowerCase()),
  )
  const parts = [
    scopes.includes('mcp') ? 'Claude Code (MCP)' : '',
    kinds.length ? kinds.join(', ') : '',
    things ? `${things} named thing${things === 1 ? '' : 's'}` : '',
    extras.join(', '),
    ws.length ? `in ${ws.map(suiteName).join(', ')}` : '',
  ].filter(Boolean)
  return parts.join('; ') || 'nothing yet'
}

const addDays = (n: number) => new Date(Date.now() + n * 86_400_000).toISOString()

export const TokenSheet = ({ token, onClose }: { token?: GrantResource; onClose: () => void }) => {
  const scopes = token?.spec.scopes ?? []
  const workspaces = useWorkspaces().filter((w) => !w.status.archived_at)
  const projects = useAllProjects() ?? []
  const pages = useAllPages() ?? []
  const lists = useAllLists() ?? []
  const [name, setName] = useState(token?.spec.name ?? '')
  const [levels, setLevels] = useState<Record<string, Level>>(() =>
    Object.fromEntries(
      TOKEN_KINDS.map((k) => [
        k.key,
        scopes.includes(`${k.key}:write`) ? 'write' : scopes.includes(`${k.key}:read`) ? 'read' : 'none',
      ]),
    ),
  )
  const [fences, setFences] = useState<string[]>(() =>
    scopes.filter((s) => s.startsWith('ws:')).map((s) => s.slice(3)),
  )
  const [things, setThings] = useState<{ kind: string; id: string; write: boolean }[]>(() =>
    scopes.flatMap((s) => {
      const m = THINGS.exec(s)
      return m ? [{ kind: m[1]!, id: m[2]!, write: m[3] === 'write' }] : []
    }),
  )
  const [extras, setExtras] = useState<string[]>(() =>
    EXTRAS.map((e) => e.scope).filter((s) => scopes.includes(s)),
  )
  const [writes, setWrites] = useState<'suggest' | 'direct'>(
    token?.spec.writes === 'direct' ? 'direct' : 'suggest',
  )
  // for an app (/api/v1) or for Claude Code (/api/mcp as well): the latter only proposes
  const [forClaude, setForClaude] = useState(() => scopes.includes('mcp'))
  const [life, setLife] = useState<'keep' | 'month' | 'quarter' | 'year' | 'forever'>(
    token ? 'keep' : 'forever',
  )
  const [find, setFind] = useState('')
  const [made, setMade] = useState<string | null>(null)
  const [copied, setCopied] = useState('')
  const [error, setError] = useState<string | null>(null)

  const titleOf = (kind: string, id: string) =>
    (kind === 'project'
      ? projects.find((p) => p.id === id)?.spec.title
      : kind === 'page'
        ? pages.find((p) => p.id === id)?.spec.title
        : lists.find((l) => l.id === id)?.spec.title) ?? 'Gone'
  const needle = find.trim().toLowerCase()
  const found = needle
    ? [
        ...projects.map((p) => ({ kind: 'project', id: p.id, title: p.spec.title })),
        ...pages.map((p) => ({ kind: 'page', id: p.id, title: p.spec.title })),
        ...lists.map((l) => ({ kind: 'list', id: l.id, title: l.spec.title })),
      ]
        .filter((x) => x.title.toLowerCase().includes(needle) && !things.some((t) => t.id === x.id))
        .slice(0, 6)
    : []
  const toggle = (xs: string[], x: string) => (xs.includes(x) ? xs.filter((y) => y !== x) : [...xs, x])

  const build = (): string[] => [
    ...TOKEN_KINDS.flatMap((k) => (levels[k.key] === 'none' ? [] : [`${k.key}:${levels[k.key]}`])),
    ...fences.map((w) => `ws:${w}`),
    ...things.map((t) => `${t.kind}:${t.id}:${t.write ? 'write' : 'read'}`),
    ...extras.filter((x) => !(forClaude && NOT_FOR_CLIENTS.has(x))),
    ...(forClaude ? ['mcp'] : []),
    ...scopes.filter((s) => s.startsWith('files-sync:')),
  ]
  // a Claude Code key's changes always wait (grant.Validate refuses direct)
  const writesNow = forClaude ? 'suggest' : writes

  const save = async () => {
    setError(null)
    if (!name.trim()) return setError('Give it a name')
    const next = build()
    if (!next.some((s) => !s.startsWith('ws:'))) return setError('Give it something to reach')
    const expires =
      life === 'month'
        ? addDays(30)
        : life === 'quarter'
          ? addDays(90)
          : life === 'year'
            ? addDays(365)
            : undefined
    try {
      if (token) {
        const patch: Record<string, unknown> = {}
        if (name.trim() !== token.spec.name) patch.name = name.trim()
        if (JSON.stringify(next) !== JSON.stringify(token.spec.scopes)) patch.scopes = next
        if (writesNow !== (token.spec.writes ?? 'suggest')) patch.writes = writesNow
        if (life !== 'keep') patch.expires_at = expires ?? null
        if (Object.keys(patch).length) await mutate.patch(token.id, { spec: patch })
        onClose()
        return
      }
      const secret = newSecret()
      await mutate.create(
        'Grant',
        {
          name: name.trim(),
          api: true,
          writes: writesNow,
          scopes: next,
          token_hash: await hashSecret(secret),
          ...(expires ? { expires_at: expires } : {}),
        },
        {},
      )
      setMade(secret)
    } catch (e) {
      setError((e as Error).message)
    }
  }

  const copy = (what: string, text: string) =>
    void navigator.clipboard
      .writeText(text)
      .then(() => setCopied(what))
      .catch(() => {})

  if (made && forClaude) {
    return (
      <Sheet
        open
        onClose={onClose}
        title="Key for Claude Code made"
        footer={
          <Button variant="primary" onClick={onClose} data-testid="token-done">
            Done
          </Button>
        }
      >
        <p className="text-sm text-muted mb-3">
          Shown once. Set these two where Claude Code runs (a shell profile); the plugin or repo carries only
          the .mcp.json below, so no key lives in it.
        </p>
        {[
          { what: 'env', label: 'Environment', text: mcpEnv(location.origin, made) },
          { what: 'mcp', label: '.mcp.json', text: MCP_JSON },
        ].map((row) => (
          <div key={row.what} className="card p-3 mb-2 flex items-start gap-2">
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-semibold text-muted">{row.label}</span>
              <pre
                className="text-xs whitespace-pre-wrap break-all text-fg-2"
                data-testid={`token-${row.what}`}
              >
                {row.text}
              </pre>
            </span>
            <Button variant="chip" onClick={() => copy(row.what, row.text)} aria-label={`Copy ${row.label}`}>
              {copied === row.what ? <Check size={14} /> : <Copy size={14} />}
            </Button>
          </div>
        ))}
        <p className="text-xs text-faint">
          Its cards and questions land on its own thread in Agents, under Claude Code, and wait for your tap.
        </p>
      </Sheet>
    )
  }

  if (made) {
    const base = `${location.origin}/api/v1`
    return (
      <Sheet
        open
        onClose={onClose}
        title="Token made"
        footer={
          <Button variant="primary" onClick={onClose} data-testid="token-done">
            Done
          </Button>
        }
      >
        <p className="text-sm text-muted mb-3">
          Shown once. Give it to {name.trim()} as a bearer token: <code>Authorization: Bearer …</code>
        </p>
        {[
          { what: 'secret', label: 'Token', text: made },
          { what: 'base', label: 'API', text: base },
          { what: 'spec', label: 'Description (OpenAPI)', text: `${base}/openapi.yaml` },
        ].map((row) => (
          <div key={row.what} className="card p-3 mb-2 flex items-center gap-2">
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-semibold text-muted">{row.label}</span>
              <code className="block text-xs break-all text-fg-2" data-testid={`token-${row.what}`}>
                {row.text}
              </code>
            </span>
            <Button variant="chip" onClick={() => copy(row.what, row.text)} aria-label={`Copy ${row.label}`}>
              {copied === row.what ? <Check size={14} /> : <Copy size={14} />}
            </Button>
          </div>
        ))}
      </Sheet>
    )
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={token ? 'Edit token' : 'New API token'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => void save()} data-testid="token-save">
            {token ? 'Save' : 'Make token'}
          </Button>
        </>
      }
    >
      <Field
        label="For"
        hint={
          forClaude
            ? 'Claude Code in any repo, through a plugin’s .mcp.json: it reads what you pick and proposes changes that wait for your tap.'
            : 'An app, a script or a bot, through /api/v1.'
        }
      >
        <Segmented
          label="For"
          value={forClaude ? 'claude' : 'app'}
          options={[
            { value: 'app', label: 'An app' },
            { value: 'claude', label: 'Claude Code (MCP)' },
          ]}
          onChange={(v) => setForClaude(v === 'claude')}
        />
      </Field>
      <Field label="Name" hint="Who holds it. Their requests show under this name.">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
          placeholder={forClaude ? 'alfieos plugin' : 'Grok'}
          className="w-full"
          data-testid="token-name"
        />
      </Field>

      <h3 className="text-[13px] font-semibold text-muted mt-4 mb-2">What it can reach</h3>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {ROLES.map((r) => (
          <Button
            key={r.key}
            variant={roleOf(levels) === r.key ? 'chipOn' : 'chip'}
            aria-pressed={roleOf(levels) === r.key}
            onClick={() =>
              setLevels(Object.fromEntries(TOKEN_KINDS.map((k) => [k.key, r.levels[k.key] ?? 'none'])))
            }
            data-testid={`token-role-${r.key}`}
          >
            {r.label}
          </Button>
        ))}
      </div>
      <ul className="group-list mb-1">
        {TOKEN_KINDS.map((k) => (
          <li key={k.key} className="flex items-center gap-3 px-3.5 py-2">
            <span className="flex-1 text-sm">{k.label}</span>
            <Segmented
              label={k.label}
              value={levels[k.key] ?? 'none'}
              options={[
                { value: 'none', label: 'None' },
                { value: 'read', label: 'Read' },
                { value: 'write', label: 'Change' },
              ]}
              onChange={(v) => setLevels((l) => ({ ...l, [k.key]: v }))}
            />
          </li>
        ))}
      </ul>
      <p className="text-xs text-faint mb-3">
        Your wellbeing, settings, links, devices and conversations with Prime are never reachable. People’s
        private notes never leave.
      </p>

      {workspaces.length > 1 && (
        <div className="mb-3">
          <span className="block text-[13px] font-semibold text-muted mb-1.5">Suites</span>
          <div className="flex flex-wrap gap-1.5">
            <Button
              variant={fences.length ? 'chip' : 'chipOn'}
              aria-pressed={!fences.length}
              onClick={() => setFences([])}
            >
              All
            </Button>
            {workspaces.map((w) => (
              <Button
                key={w.id}
                variant={fences.includes(w.spec.slug) ? 'chipOn' : 'chip'}
                aria-pressed={fences.includes(w.spec.slug)}
                onClick={() => setFences((f) => toggle(f, w.spec.slug))}
                data-testid={`token-ws-${w.spec.slug}`}
              >
                {w.spec.name}
              </Button>
            ))}
          </div>
          <p className="text-xs text-faint mt-1">
            Tasks, projects, pages and lists outside these stay hidden.
          </p>
        </div>
      )}

      <Field
        label="Specific things"
        hint="A project, page or list, and everything under it, whatever is set above."
      >
        <input
          value={find}
          onChange={(e) => setFind(e.target.value)}
          placeholder="Find a project, page or list"
          className="w-full"
          data-testid="token-find"
        />
      </Field>
      {found.length > 0 && (
        <ul className="group-list mb-2">
          {found.map((x) => (
            <li key={x.id}>
              <button
                type="button"
                className="w-full text-left px-3.5 py-2 text-sm"
                onClick={() => {
                  setThings((t) => [...t, { kind: x.kind, id: x.id, write: false }])
                  setFind('')
                }}
              >
                {x.title} <span className="text-faint ml-2">{x.kind}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {things.length > 0 && (
        <ul className="group-list mb-3">
          {things.map((t) => (
            <li key={t.id} className="flex items-center gap-2 px-3.5 py-2">
              <span className="min-w-0 flex-1 truncate text-sm">
                {titleOf(t.kind, t.id)} <span className="text-faint ml-2">{t.kind}</span>
              </span>
              <Segmented
                label={titleOf(t.kind, t.id)}
                value={t.write ? 'write' : 'read'}
                options={[
                  { value: 'read', label: 'Read' },
                  { value: 'write', label: 'Change' },
                ]}
                onChange={(v) =>
                  setThings((all) => all.map((x) => (x.id === t.id ? { ...x, write: v === 'write' } : x)))
                }
              />
              <Button variant="quiet" onClick={() => setThings((all) => all.filter((x) => x.id !== t.id))}>
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="mb-3">
        <span className="block text-[13px] font-semibold text-muted mb-1.5">Also</span>
        <ul className="group-list">
          {EXTRAS.filter((x) => !(forClaude && NOT_FOR_CLIENTS.has(x.scope))).map((x) => (
            <li key={x.scope}>
              <label className="flex items-start gap-3 px-3.5 py-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={extras.includes(x.scope)}
                  onChange={() => setExtras((e) => toggle(e, x.scope))}
                  className="mt-0.5"
                  data-testid={`token-extra-${x.scope}`}
                />
                <span>
                  <span className="block text-sm">{x.label}</span>
                  <span className="block text-xs text-faint">{x.hint}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      </div>

      {forClaude ? (
        <Field label="Its changes">
          <p className="text-sm text-muted" data-testid="token-client-changes">
            Always cards on its own thread in Agents, waiting for your tap. Nothing applies by itself.
          </p>
        </Field>
      ) : (
        <Field
          label="Its changes"
          hint={
            writes === 'suggest'
              ? 'Each change waits in your Inbox until you say yes.'
              : 'Changes apply at once, under its name in History. Deletes go to the Trash.'
          }
        >
          <Segmented
            label="Its changes"
            value={writes}
            options={[
              { value: 'suggest', label: 'Wait for me' },
              { value: 'direct', label: 'Apply at once' },
            ]}
            onChange={setWrites}
          />
        </Field>
      )}
      <Field label="Lasts">
        <Segmented
          label="Lasts"
          value={life}
          options={[
            ...(token ? [{ value: 'keep' as const, label: 'As is' }] : []),
            { value: 'month', label: '30 days' },
            { value: 'quarter', label: '90 days' },
            { value: 'year', label: 'A year' },
            { value: 'forever', label: 'Until revoked' },
          ]}
          onChange={setLife}
        />
      </Field>
      {error && <p className="text-xs text-amber-300/90 mb-2">{error}</p>}
    </Sheet>
  )
}
